import { useState, useEffect, useRef } from "react";

function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calc = () => {
      const diff = targetDate.getTime() - Date.now();
      if (diff <= 0) return setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return timeLeft;
}

function EmailModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const body = new URLSearchParams({ email: email.trim() });
      const res = await fetch("https://readdy.ai/api/form/d72pabpsakbatd8b3olg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
    >
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 animate-fade-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors cursor-pointer rounded-full hover:bg-gray-100"
          aria-label="Fechar"
        >
          <i className="ri-close-line text-xl" />
        </button>

        {status === "success" ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 rounded-full bg-[#E8F6EE]">
              <i className="ri-mail-check-line text-3xl text-[#008C51]" />
            </div>
            <h3 className="text-xl font-extrabold text-gray-900 mb-2">Tudo certo!</h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              Anotamos seu e-mail. Assim que as inscrições abrirem, você será o primeiro a saber!
            </p>
            <button
              onClick={onClose}
              className="mt-6 bg-[#008C51] text-white font-bold px-8 py-3 rounded-full hover:bg-[#006e3f] transition-colors cursor-pointer whitespace-nowrap"
            >
              Fechar
            </button>
          </div>
        ) : (
          <>
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5 rounded-full bg-[#E8F6EE]">
              <i className="ri-notification-3-line text-2xl text-[#008C51]" />
            </div>
            <h3 className="text-xl font-extrabold text-gray-900 text-center mb-1">Quero ser avisado!</h3>
            <p className="text-gray-500 text-sm text-center mb-7 leading-relaxed">
              Deixa seu e-mail e a gente avisa assim que as inscrições abrirem. É gratuito!
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 pointer-events-none">
                  <i className="ri-mail-line text-base" />
                </span>
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="w-full pl-11 pr-4 py-3.5 rounded-full border border-gray-200 text-sm focus:outline-none focus:border-[#008C51] focus:ring-2 focus:ring-[#008C51]/20 transition"
                />
              </div>

              {status === "error" && (
                <p className="text-red-500 text-xs text-center -mt-1">
                  Ops, algo deu errado. Tente novamente.
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="bg-[#008C51] text-white font-extrabold text-sm px-8 py-3.5 rounded-full hover:bg-[#006e3f] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" />
                    Me avisa!
                  </>
                )}
              </button>
            </form>

            <p className="text-gray-400 text-xs text-center mt-4">
              Sem spam. Só um aviso quando abrir.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default function FinalBanner() {
  const eventDate = new Date("2026-04-12T07:30:00-03:00");
  const regDate = new Date("2026-04-02T00:00:00-03:00");
  const countdown = useCountdown(eventDate);
  const now = new Date();
  const regOpen = now >= regDate;
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      {showModal && <EmailModal onClose={() => setShowModal(false)} />}

      <section
        id="inscricoes"
        className="relative py-24 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #008C51 0%, #BCD033 45%, #A4D8DE 75%, #F0CDCD 100%)" }}
      >
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }} />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <span className="inline-block bg-white/20 backdrop-blur text-white text-xs font-bold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase border border-white/30">
            3ª Edição · 12 de Abril de 2026
          </span>

          <h2 className="text-4xl md:text-6xl font-extrabold leading-tight mb-4 drop-shadow">
            Pronto para começar?
          </h2>
          <p className="text-white/90 text-lg mb-10 max-w-xl mx-auto">
            {regOpen
              ? "As inscrições estão abertas! Garanta já a sua vaga — o evento é gratuito."
              : "As inscrições abrem em 02/04/2026 às 10h00. Fique ligado!"}
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {[
              { label: "Dias", value: countdown.days },
              { label: "Horas", value: countdown.hours },
              { label: "Min", value: countdown.minutes },
              { label: "Seg", value: countdown.seconds },
            ].map((unit) => (
              <div
                key={unit.label}
                className="bg-white/20 backdrop-blur border border-white/30 rounded-2xl px-5 py-4 min-w-[80px]"
              >
                <p className="text-4xl font-extrabold tabular-nums leading-none">
                  {String(unit.value).padStart(2, "0")}
                </p>
                <p className="text-white/70 text-xs mt-1 font-semibold tracking-wider uppercase">{unit.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <span
              className="bg-white/50 text-white font-extrabold text-base px-8 py-4 rounded-full cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-ticket-2-fill" />
              Inscrições encerradas
            </span>
            <a
              href="#cronograma"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("cronograma")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="border-2 border-white text-white font-bold text-base px-8 py-4 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-calendar-schedule-line" />
              Ver Programação
            </a>
          </div>
        </div>
      </section>


      <footer className="bg-[#008C51] py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-center md:items-start gap-2">
            <span className="text-white font-extrabold text-lg tracking-wide">Mude1Hábito Experience</span>
            <p className="text-white/50 text-xs">3ª Edição · 12 de Abril de 2026 · Belo Horizonte</p>
          </div>

          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-white/80 text-sm font-semibold">Arena 4Set – Buritis</p>
            <p className="text-white/50 text-xs">R. Líbero Leone, 259 – Buritis, BH</p>
            <p className="text-white/50 text-xs">07h30 às 12h00 · Domingo</p>
          </div>
        </div>
        <div className="border-t border-white/10 mt-8 pt-6 text-center">
          <p className="text-white/30 text-xs">
            &copy; 2026 Mude1Hábito Experience. Evento gratuito, sujeito à inscrição prévia.
          </p>
        </div>
      </footer>
    </>
  );
}
